import { custom } from 'devextreme/ui/dialog';
import { readShellLayoutState } from '../shell/useShellLayout';

export interface ConfirmDialogOptions {
  title?: string;
  message?: string;
  messageHtml?: string;
  confirmText?: string;
  cancelText?: string;
  /** When true, the primary button uses danger styling (for destructive actions). */
  danger?: boolean;
}

/** DevExtreme confirm dialog — matches the pattern used in the file browser and editors. */
export async function showConfirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  const {
    title = 'Confirm',
    message,
    messageHtml,
    confirmText = 'OK',
    cancelText = 'Cancel',
    danger = false
  } = options;

  const isNarrow = readShellLayoutState().isNarrow;

  const dialog = custom({
    title,
    messageHtml: messageHtml ?? escapeHtml(message ?? ''),
    ...(isNarrow ? { width: '100vw', maxWidth: '100vw', height: 'auto' } : {}),
    buttons: [
      {
        text: confirmText,
        type: danger ? 'danger' : 'default',
        stylingMode: 'contained',
        onClick: () => true
      },
      {
        text: cancelText,
        stylingMode: 'outlined',
        onClick: () => false
      }
    ]
  });

  try {
    return (await dialog.show()) === true;
  } catch {
    return false;
  }
}

export function showDeleteConfirmDialog(message: string): Promise<boolean> {
  return showConfirmDialog({
    title: 'Confirm delete',
    message,
    confirmText: 'Delete',
    danger: true
  });
}

/** Confirm client-user off-board (disable + drain groups/license; does not delete the account). */
export function showOffboardConfirmDialog(displayName: string, userPrincipalName: string): Promise<boolean> {
  const name = escapeHtml(displayName);
  const upn = escapeHtml(userPrincipalName);
  return showConfirmDialog({
    title: 'Confirm off-board',
    messageHtml: `
      <div style="line-height:1.45">
        <p style="margin:0 0 0.75rem">Off-board <strong>${name}</strong> (<code>${upn}</code>)?</p>
        <p style="margin:0 0 0.35rem"><strong>This will:</strong></p>
        <ul style="margin:0 0 0.75rem;padding-left:1.25rem">
          <li>Remove all managed SPR security groups</li>
          <li>Remove Entra ID P2 license (if assigned)</li>
          <li>Revoke active sign-in sessions</li>
          <li>Disable the Entra account</li>
          <li>Email configured off-boarding notify contacts</li>
        </ul>
        <p style="margin:0;opacity:0.85">The user account will not be deleted.</p>
      </div>
    `,
    confirmText: 'Off-board',
    danger: true
  });
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
